import { getRandomInt } from "../Shared/Helpers.tsx";
import { generateProblemPages } from "../Shared/Generation/problemPages.ts";
import {
    Fraction,
    FractionDenominatorOptions,
    FractionQuestionOptions,
    isFractionModelQuestion,
    Operations,
    ProblemData,
} from "./Constants.ts";
import { ISettingsProps } from "./Models.ts";

/**
 * Fraction problem generation.
 *
 * Kept apart from ProblemGenerator because almost nothing is shared: whole
 * number problems are built from the selected digit buckets, while these are
 * built from a denominator limit and a rule about whether the two denominators
 * match. GenerateDataV2 delegates here when the worksheet is a fraction sheet.
 */

/** Euclid. Used to put every answer in lowest terms. */
export function greatestCommonDivisor(a: number, b: number): number {
    let x = Math.abs(a);
    let y = Math.abs(b);
    while (y !== 0) {
        [x, y] = [y, x % y];
    }
    return x === 0 ? 1 : x;
}

/** A fraction in lowest terms, keeping it improper. */
export function simplify(numerator: number, denominator: number): Fraction {
    const divisor = greatestCommonDivisor(numerator, denominator);
    return { numerator: numerator / divisor, denominator: denominator / divisor };
}

/**
 * The way an answer is written on a worksheet: lowest terms, and as a mixed
 * number rather than an improper fraction. 6/4 becomes 1 1/2; 8/4 becomes 2.
 *
 * Works on the absolute value and puts the sign back (ADR 0122 §5), because a
 * floor-and-remainder on a negative numerator is wrong: -5/4 floors to -2 with a
 * remainder of -1. The sign belongs to the whole mixed number, so -5/4 is
 * `{ whole: -1, numerator: 1, denominator: 4 }` - written -1 1/4, meaning
 * -(1 + 1/4) - and with no whole part it rides the numerator. For anything
 * not negative this is exactly what it always returned.
 */
export function toMixedNumber(numerator: number, denominator: number): Fraction {
    const negative = numerator !== 0 && (numerator < 0) !== (denominator < 0);
    const reduced = simplify(Math.abs(numerator), Math.abs(denominator));
    const whole = Math.floor(reduced.numerator / reduced.denominator);
    const remainder = reduced.numerator % reduced.denominator;

    if (whole === 0) {
        return { numerator: negative ? -remainder : remainder, denominator: reduced.denominator };
    }
    if (remainder === 0) {
        // A whole number. Numerator 0 tells the renderer to draw no fraction.
        return { whole: negative ? -whole : whole, numerator: 0, denominator: 1 };
    }
    return { whole: negative ? -whole : whole, numerator: remainder, denominator: reduced.denominator };
}

/** True when the fraction is a bare whole number rather than a fraction. */
export function isWholeNumber(fraction: Fraction): boolean {
    return fraction.numerator === 0;
}

/** Decimal value, for ordering operands so a subtraction cannot go negative. */
function valueOf(fraction: Fraction): number {
    return (fraction.whole ?? 0) + fraction.numerator / fraction.denominator;
}

/** A proper fraction with a denominator of at least 2, in lowest terms or not. */
function randomProperFraction(denominator: number): Fraction {
    return { numerator: getRandomInt(1, denominator - 1), denominator };
}

/**
 * Picks the pair of denominators for one problem.
 *
 * `Both` is resolved per problem rather than per sheet, so a mixed page really
 * does alternate rather than settling on one kind.
 */
function pickDenominators(
    rule: FractionDenominatorOptions,
    maxDenominator: number
): [number, number] {
    const first = getRandomInt(2, maxDenominator);

    const wantsLike =
        rule === FractionDenominatorOptions.Like ||
        (rule === FractionDenominatorOptions.Both && getRandomInt(0, 1) === 0);

    if (wantsLike) {
        return [first, first];
    }

    // Unlike: keep drawing until the two differ. With a max of 2 there is only
    // one denominator available, so fall back to like rather than loop forever.
    if (maxDenominator < 3) {
        return [first, first];
    }
    let second = getRandomInt(2, maxDenominator);
    while (second === first) {
        second = getRandomInt(2, maxDenominator);
    }
    return [first, second];
}

/**
 * One fraction problem, or null when the draw produced something unusable
 * (a subtraction of equal fractions, which is always zero and teaches nothing).
 */
export function buildFractionProblem(
    operator: Operations,
    rule: FractionDenominatorOptions,
    maxDenominator: number,
    key: number
): ProblemData | null {
    // Multiplication and division do not need a common denominator, so the
    // like/unlike rule does not apply to them - they always draw freely.
    const needsCommonDenominator =
        operator === Operations.Addition || operator === Operations.Subtraction;

    const [firstDenominator, secondDenominator] = needsCommonDenominator
        ? pickDenominators(rule, maxDenominator)
        : [getRandomInt(2, maxDenominator), getRandomInt(2, maxDenominator)];

    let left = randomProperFraction(firstDenominator);
    let right = randomProperFraction(secondDenominator);

    let answerNumerator: number;
    let answerDenominator: number;

    switch (operator) {
        case Operations.Addition: {
            answerNumerator =
                left.numerator * right.denominator + right.numerator * left.denominator;
            answerDenominator = left.denominator * right.denominator;
            break;
        }
        case Operations.Subtraction: {
            // Order the operands so the answer is never negative.
            if (valueOf(right) > valueOf(left)) {
                [left, right] = [right, left];
            }
            if (valueOf(left) === valueOf(right)) {
                return null;
            }
            answerNumerator =
                left.numerator * right.denominator - right.numerator * left.denominator;
            answerDenominator = left.denominator * right.denominator;
            break;
        }
        case Operations.Multiplication: {
            answerNumerator = left.numerator * right.numerator;
            answerDenominator = left.denominator * right.denominator;
            break;
        }
        case Operations.Division: {
            answerNumerator = left.numerator * right.denominator;
            answerDenominator = left.denominator * right.numerator;
            break;
        }
        default:
            return null;
    }

    return {
        key,
        numbers: [],
        operator: operatorSign(operator),
        fractions: [left, right, toMixedNumber(answerNumerator, answerDenominator)],
    };
}

// ─── fraction sub-skills (ADR 0021) ─────────────────────────────────────────
//
// Each question type has a valid draw that is pedagogically worthless, and each
// needs an explicit rule - the same discipline ADR 0006 §4 applied to
// comparison pairs. Rejected draws return null and are redrawn.

/** Denominators whose only prime factors are 2 and 5, so the decimal terminates. */
const TERMINATING_DENOMINATORS = [2, 4, 5, 8, 10];

function coprimeFraction(maxDenominator: number): Fraction {
    // A proper fraction already in lowest terms.
    let denominator = getRandomInt(2, maxDenominator);
    let numerator = getRandomInt(1, denominator - 1);
    while (greatestCommonDivisor(numerator, denominator) !== 1) {
        denominator = getRandomInt(2, maxDenominator);
        numerator = getRandomInt(1, denominator - 1);
    }
    return { numerator, denominator };
}

function decimalValue(numerator: number, denominator: number): string {
    return String(numerator / denominator);
}

export function buildFractionSubskillProblem(
    question: FractionQuestionOptions,
    maxDenominator: number,
    key: number
): ProblemData | null {
    const base = (fractions: Fraction[], extra: Partial<ProblemData> = {}): ProblemData => ({
        key,
        numbers: [],
        operator: "",
        fractions,
        fractionQuestion: question,
        ...extra,
    });

    switch (question) {
        case FractionQuestionOptions.Simplify: {
            const reduced = coprimeFraction(Math.floor(maxDenominator / 2) || 2);
            const factor = getRandomInt(2, Math.floor(maxDenominator / reduced.denominator));
            if (factor < 2) return null; // gcd would be 1 - nothing to simplify
            const given = {
                numerator: reduced.numerator * factor,
                denominator: reduced.denominator * factor,
            };
            return base([given, given, reduced]);
        }
        case FractionQuestionOptions.Equivalent: {
            const given = coprimeFraction(Math.floor(maxDenominator / 2) || 2);
            const multiple = getRandomInt(2, Math.floor(maxDenominator / given.denominator));
            if (multiple < 2) return null;
            const target = { numerator: 0, denominator: given.denominator * multiple };
            const answer = {
                numerator: given.numerator * multiple,
                denominator: given.denominator * multiple,
            };
            return base([given, target, answer]);
        }
        case FractionQuestionOptions.Compare: {
            const left = coprimeFraction(maxDenominator);
            // Equality one time in ten, and it means 2/4 = 1/2 - the interesting case.
            if (getRandomInt(1, 10) === 1) {
                const m = getRandomInt(2, Math.max(2, Math.floor(maxDenominator / left.denominator)));
                const right = { numerator: left.numerator * m, denominator: left.denominator * m };
                if (right.denominator > maxDenominator) return null;
                return base([left, right], { display: ["="] });
            }
            let right = coprimeFraction(maxDenominator);
            if (right.denominator === left.denominator) return null; // unlike denominators
            const lv = left.numerator / left.denominator;
            const rv = right.numerator / right.denominator;
            if (lv === rv) return null;
            // Close enough that a method is needed, not eyeballing.
            if (Math.abs(lv - rv) > 0.2) return null;
            return base([left, right], { display: [lv > rv ? ">" : "<"] });
        }
        case FractionQuestionOptions.MixedAndImproper: {
            const denominator = getRandomInt(2, maxDenominator);
            const whole = getRandomInt(1, 4);
            const part = getRandomInt(1, denominator - 1); // proper fractional part
            const improper = { numerator: whole * denominator + part, denominator };
            const mixed = { whole, numerator: part, denominator };
            return base([improper, mixed]);
        }
        case FractionQuestionOptions.FractionOfANumber: {
            const fraction = coprimeFraction(maxDenominator);
            const k = getRandomInt(2, 8);
            const number = fraction.denominator * k; // divisible by the denominator
            const answer = fraction.numerator * k;
            return base([fraction], { fractionOfNumber: number, numbers: [number, answer] });
        }
        case FractionQuestionOptions.ToDecimal: {
            // MaxDenominator is filtered, not honoured (ADR 0021 §2).
            const admissible = TERMINATING_DENOMINATORS.filter((d) => d <= maxDenominator);
            const denominator = admissible[getRandomInt(0, admissible.length - 1)];
            const numerator = getRandomInt(1, denominator - 1);
            const given = { numerator, denominator };
            return base([given], { display: [decimalValue(numerator, denominator)] });
        }
        default:
            return null;
    }
}

// ─── fraction models (ADR 0043) ─────────────────────────────────────────────
//
// A picture is the prompt rather than the decoration. Both question types
// share the shape `buildFractionSubskillProblem` already uses: one or two
// `Fraction`s on `fractions`, a `fractionQuestion` tag, and - for
// CompareModels - the comparison symbol on `display[0]`, exactly the way
// `Compare` already writes it. Nothing here simplifies an answer: a picture of
// four parts with two shaded answers `2/4`, not `1/2` (ADR 0043 §Judgement
// calls) - simplifying is Simplify's job, not this one's.

/** A proper fraction with a denominator of at least 2, not necessarily reduced. */
function randomModelFraction(maxDenominator: number): Fraction {
    const denominator = getRandomInt(2, Math.max(2, maxDenominator));
    return { numerator: getRandomInt(1, denominator), denominator };
}

/**
 * Picks the pair of denominators for one comparison.
 *
 * Different most of the time - two bars with the same number of divisions is
 * a counting exercise, not a comparison (ADR 0043 §1) - but not forced, so a
 * sheet is not artificially prevented from ever showing two same-denominator
 * bars either.
 */
function pickModelDenominators(maxDenominator: number): [number, number] {
    const bound = Math.max(2, maxDenominator);
    const first = getRandomInt(2, bound);
    if (bound < 3) {
        return [first, first];
    }
    const wantsSame = getRandomInt(1, 5) === 1;
    if (wantsSame) {
        return [first, first];
    }
    let second = getRandomInt(2, bound);
    while (second === first) {
        second = getRandomInt(2, bound);
    }
    return [first, second];
}

/**
 * One model, unlabelled: the picture is the whole question, and the answer
 * (numerator/denominator) is read straight off `fractions[0]` - never
 * simplified, since "what fraction is shaded" and "what is this in lowest
 * terms" are different questions (Simplify already asks the second one).
 */
export function buildNameTheModelProblem(maxDenominator: number, key: number): ProblemData | null {
    const model = randomModelFraction(maxDenominator);
    return {
        key,
        numbers: [],
        operator: "",
        fractions: [model],
        fractionQuestion: FractionQuestionOptions.NameTheModel,
    };
}

/**
 * Two models, compared. `=` is reachable - about one draw in ten, the same
 * rate `buildFractionSubskillProblem`'s Compare case uses - by construction
 * rather than by chance: a fraction and a genuine equivalent of it at a
 * different denominator (2/4 against 1/2), so the two bars have different
 * numbers of divisions but the same shaded length. Every other draw compares
 * two independently-chosen models and rejects an accidental tie, so the
 * reported "about one in ten" rate means what it says.
 */
export function buildCompareModelsProblem(maxDenominator: number, key: number): ProblemData | null {
    // The equal pair needs a left denominator that can still be doubled. Drawing
    // it from the whole range and rejecting what did not fit threw away every
    // draw above half the maximum, so "one in ten" was really about one in
    // sixteen. With no room at all (a maximum under 4) there is no equal pair
    // to draw, and the draw is an ordinary comparison.
    const room = Math.floor(maxDenominator / 2);
    if (room >= 2 && getRandomInt(1, 10) === 1) {
        const left = randomModelFraction(room);
        const scale = getRandomInt(2, Math.floor(maxDenominator / left.denominator));
        const right = { numerator: left.numerator * scale, denominator: left.denominator * scale };
        return {
            key,
            numbers: [],
            operator: "",
            fractions: [left, right],
            fractionQuestion: FractionQuestionOptions.CompareModels,
            display: ["="],
        };
    }

    const [firstDenominator, secondDenominator] = pickModelDenominators(maxDenominator);
    const left: Fraction = { numerator: getRandomInt(1, firstDenominator), denominator: firstDenominator };
    const right: Fraction = { numerator: getRandomInt(1, secondDenominator), denominator: secondDenominator };

    const lv = left.numerator / left.denominator;
    const rv = right.numerator / right.denominator;
    if (lv === rv) {
        // Only the explicit branch above is allowed to produce an equal pair,
        // so the "about one in ten" rate means what it says.
        return null;
    }

    return {
        key,
        numbers: [],
        operator: "",
        fractions: [left, right],
        fractionQuestion: FractionQuestionOptions.CompareModels,
        display: [lv > rv ? ">" : "<"],
    };
}

// ─── mixes (ADR 0045) ────────────────────────────────────────────────────
//
// Each mix expands to a fixed list of existing sub-skills - matching
// NumberPropertyGenerator's `questionTypes` shape (ADR 0041 §3) - rather than
// adding a third arm to generateFractionPages's branch. The fair rotation
// over the expanded list comes from generateProblemPages's `choices`
// (ADR 0041 §1); nothing here writes one.

/**
 * Which notation sub-skills `MixedSkills` draws from. Every other sub-skill
 * setting (including `MixedModels`, handled separately below) draws only
 * itself. `Arithmetic` never reaches this function.
 */
function subskillTypes(question: FractionQuestionOptions): FractionQuestionOptions[] {
    if (question === FractionQuestionOptions.MixedSkills) {
        return [
            FractionQuestionOptions.Simplify,
            FractionQuestionOptions.Equivalent,
            FractionQuestionOptions.Compare,
            FractionQuestionOptions.MixedAndImproper,
            FractionQuestionOptions.FractionOfANumber,
            FractionQuestionOptions.ToDecimal,
        ];
    }
    return [question];
}

/** Which of the two model types `MixedModels` draws from. */
function modelTypes(question: FractionQuestionOptions): FractionQuestionOptions[] {
    if (question === FractionQuestionOptions.MixedModels) {
        return [FractionQuestionOptions.NameTheModel, FractionQuestionOptions.CompareModels];
    }
    return [question];
}

function operatorSign(operator: Operations): string {
    switch (operator) {
        case Operations.Addition:
            return "+";
        case Operations.Subtraction:
            return "-";
        case Operations.Multiplication:
            return "x";
        default:
            return "÷";
    }
}

/** Two problems are the same sheet-wise if both operands and the sign match. */
function fingerprint(problem: ProblemData): string {
    const [left, right] = problem.fractions!;
    return `${left.numerator}/${left.denominator}${problem.operator}${right.numerator}/${right.denominator}`;
}

/** Two sub-skill problems are the same if the question and every fraction shown match. */
function subskillFingerprint(problem: ProblemData): string {
    const parts = problem
        .fractions!.map((f) => `${f.whole ?? 0}.${f.numerator}/${f.denominator}`)
        .join('|');
    return `${problem.fractionQuestion}:${parts}:${problem.fractionOfNumber ?? ''}`;
}

/**
 * A page-shaped set of fraction problems, matching what GenerateDataV2 returns
 * for whole numbers: one array per page, each holding NumberOfProblemsPerPage.
 *
 * Arithmetic and the sub-skills (ADR 0021) are different enough - a different
 * fingerprint, and a `choices` list of operators versus a single fixed
 * question - that this picks its branch once and calls the shared loop from
 * whichever one applies, rather than giving the loop a branch of its own
 * (ADR 0042 §4: a parameter for one caller is worse than the duplication it
 * replaces). Only one branch ever runs for a given sheet, matching the
 * original behaviour exactly.
 */
export function generateFractionPages(settings: ISettingsProps): ProblemData[][] | null {
    const operators = settings.Operators?.map((operator) => operator.value) ?? [];
    if (operators.length === 0) {
        return null;
    }

    const maxDenominator = settings.MaxDenominator ?? 12;
    const rule = settings.FractionDenominators ?? FractionDenominatorOptions.Both;
    const question = settings.FractionQuestion ?? FractionQuestionOptions.Arithmetic;

    // The two visual-model questions (ADR 0043) read their own denominator
    // bound - FractionModelMaxDenominator, not MaxDenominator - because the
    // bound is about keeping the drawn cells countable, not about arithmetic
    // difficulty, and the two are set independently.
    if (isFractionModelQuestion(question)) {
        const maxModelDenominator = settings.FractionModelMaxDenominator ?? 6;
        return generateProblemPages({
            settings,
            choices: modelTypes(question),
            build: (chosenQuestion, key) =>
                chosenQuestion === FractionQuestionOptions.NameTheModel
                    ? buildNameTheModelProblem(maxModelDenominator, key)
                    : buildCompareModelsProblem(maxModelDenominator, key),
            fingerprint: subskillFingerprint,
        });
    }

    if (question !== FractionQuestionOptions.Arithmetic) {
        return generateProblemPages({
            settings,
            choices: subskillTypes(question),
            build: (chosenQuestion, key) =>
                buildFractionSubskillProblem(chosenQuestion, maxDenominator, key),
            fingerprint: subskillFingerprint,
        });
    }

    return generateProblemPages({
        settings,
        choices: operators,
        build: (operator, key) => buildFractionProblem(operator, rule, maxDenominator, key),
        fingerprint,
    });
}
